import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { ChevronLeft, ChevronRight, Plus, Search, X } from "lucide-react";
import { api } from "../lib/api";
import { useResource } from "../lib/schema";
import ResourceTable from "../components/ResourceTable";
import type { ListResponse } from "../lib/types";
import { number } from "../lib/format";

// One screen for every table in the registry. Search, filters, sort and page
// live in the URL so an operator can paste a view straight into Slack and the
// person on the other end sees exactly what they saw.
export default function ResourceList() {
  const { key } = useParams<{ key: string }>();
  const { resource, isLoading: schemaLoading } = useResource(key);
  const [params, setParams] = useSearchParams();

  const page = Number(params.get("page") || 1);
  const sort = params.get("sort") || resource?.default_sort.column;
  const direction = params.get("direction") || resource?.default_sort.direction || "desc";
  const q = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(q);

  const filters = useMemo(() => {
    const active: Record<string, string> = {};
    for (const [name, value] of params.entries()) {
      const match = name.match(/^filters\[(.+)\]$/);
      if (match && value) active[match[1]] = value;
    }
    return active;
  }, [params]);

  useEffect(() => setSearchInput(q), [q]);

  const setParam = (name: string, value: string | null) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    if (name !== "page") next.delete("page");
    setParams(next);
  };

  const { data, isFetching } = useQuery({
    queryKey: ["resource", key, page, sort, direction, q, filters],
    queryFn: () =>
      api.get<ListResponse>(`/superadmin/resources/${key}`, {
        page, per_page: 50, sort, direction, q: q || undefined, filters,
      }),
    enabled: Boolean(key && resource),
    placeholderData: keepPreviousData,
  });

  if (schemaLoading) return <div className="panel h-96 animate-pulse" />;
  if (!resource) return <div className="panel p-6 text-sm text-ink-400">Okänd resurs: {key}</div>;

  const toggleSort = (column: string) => {
    if (sort === column) setParam("direction", direction === "asc" ? "desc" : "asc");
    else {
      const next = new URLSearchParams(params);
      next.set("sort", column);
      next.set("direction", "desc");
      next.delete("page");
      setParams(next);
    }
  };

  const activeFilterCount = Object.keys(filters).length + (q ? 1 : 0);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-white">{resource.label}</h1>
          <p className="text-sm text-ink-500">
            {data ? `${number(data.meta.total)} rader` : "Laddar…"}
            {activeFilterCount > 0 && " (filtrerat)"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {resource.creatable.length > 0 && (
            <Link to={`/r/${key}/new`} className="btn-primary">
              <Plus className="h-3.5 w-3.5" /> {resource.new_label || "Ny"}
            </Link>
          )}
          {resource.search && (
          <form
            onSubmit={(e) => { e.preventDefault(); setParam("q", searchInput || null); }}
            className="relative"
          >
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-500" />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Sök…"
              className="field w-56 pl-8"
            />
            {q && (
              <button
                type="button"
                onClick={() => { setSearchInput(""); setParam("q", null); }}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-300"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </form>
          )}
        </div>
      </div>

      {Object.keys(resource.filters).length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {Object.entries(resource.filters).map(([name, filter]) => (
            <select
              key={name}
              value={filters[name] || ""}
              onChange={(e) => setParam(`filters[${name}]`, e.target.value || null)}
              className="field w-auto py-1.5 text-xs"
            >
              <option value="">{filter.label}: alla</option>
              {filter.options.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          ))}
          {activeFilterCount > 0 && (
            <button onClick={() => setParams(new URLSearchParams())} className="text-xs text-ink-500 hover:text-ink-300">
              Rensa allt
            </button>
          )}
        </div>
      )}

      <ResourceTable
        resourceKey={resource.key}
        columns={resource.columns}
        rows={data?.rows || []}
        isFetching={isFetching}
        sort={sort}
        direction={direction}
        onSort={toggleSort}
      />

      {data && data.meta.pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <span className="text-ink-500 num">
            Sida {data.meta.page} av {number(data.meta.pages)}
          </span>
          <div className="flex gap-2">
            <button
              disabled={page <= 1}
              onClick={() => setParam("page", String(page - 1))}
              className="btn-ghost"
            >
              <ChevronLeft className="h-3.5 w-3.5" /> Föregående
            </button>
            <button
              disabled={page >= data.meta.pages}
              onClick={() => setParam("page", String(page + 1))}
              className="btn-ghost"
            >
              Nästa <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}

      <Link to="/activity" className="inline-block text-xs text-ink-600 hover:text-ink-400">
        ← Tillbaka till aktivitetsflödet
      </Link>
    </div>
  );
}
